import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArcherContainer, ArcherElement } from 'react-archer';

import {
  Heading
  // Loading
} from 'components/common/Elements/Elements';
import NetworkWidget from '../../../NetworkWidget/NetworkWidget';
import { postRequest } from 'utils/requests';
import classes from './Graf.module.scss';

interface GrafProps {
  selectId: string;
  customerId: string;
  isGrafBlocked: boolean;
  setShowProgressBar: (show: boolean) => void;
}

interface NetworkTunnel {
  tunnelId: string;
  tunnelName: string;
  tunnelIp: string;
  tunnelType: string;
  isBeaconStatusExist?: boolean;
  isHa?: boolean;
  connectedToGW: boolean;
}

interface NetworkGateway {
  gatewayId: string;
  gatewayDns: string;
  gatewayIp: string;
  gatewayImageVersion: string;
  instanceType: string;
  isGatewayDisabled: boolean;
  isCpuResizeEnabled: boolean;
  instancePlanType?: string | null;
  availableInstancePlanTypes?: string[];
  tunnels: NetworkTunnel[];
  isBeaconStatusExist?: boolean;
  beaconStatus?: any;
}

interface NetworkRegion {
  regionId: string;
  providerRegionName: string;
  regionDns?: string;
  regionPublicIp?: string[];
  regionCountry: string;
  regionStatus: string;
  regionScaleUnits: number;
  networkVersion: string;
  gateways: NetworkGateway[];
}

const Graf: React.FC<GrafProps> = ({
  selectId,
  customerId,
  setShowProgressBar,
  isGrafBlocked
}) => {
  const [networkInfo, setNetworkInfo] = useState<NetworkRegion[]>([]);

  const { t } = useTranslation(['activity']);

  useEffect(() => {
    let cancelled = false;
    postRequest('/customer/platform/network/info', {
      networkId: selectId
    })
      .then(res => {
        if (!cancelled) {
          if (res.body && res.body.regions != 'undefined') {
            setNetworkInfo(res.body.regions);
          }
        }
      })
      .catch(err => {
        !cancelled && console.log(err);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [selectId]);

  return (
    <div style={{ position: 'relative', minHeight: '400px' }}>
      <ArcherContainer
        endMarker={false}
        strokeWidth={2}
        strokeColor="#7f8c9b"
        strokeDasharray="5,5"
        style={{ width: '100%', height: '100%' }}
      >
        <div className={classes.bodyNetworkWraper}>
          {isGrafBlocked && <div className={classes.bodyGrafHidden}></div>}
          <div className={classes.bodyCellWrapTitle}>
            <Heading className={classes.title}>
              {t('activity:platform.REGIONS')}
            </Heading>
            <Heading className={classes.title}>
              {t('activity:platform.GATEWAYS')}
            </Heading>
            <Heading className={classes.title}>
              {t('activity:platform.TUNNELS')}
            </Heading>
          </div>
          {networkInfo.map(region => (
            <div key={`wrap=${region.regionId}`} className={classes.rowWrap}>
              <div className={classes.widjetWrap}>
                <ArcherElement
                  id={`region-${region.regionId}`}
                  relations={region.gateways.map(gateway => ({
                    targetId: `gateway-${gateway.gatewayId}`,
                    sourceAnchor: 'left',
                    targetAnchor: 'left',
                    style: {
                      strokeColor: '#7f8c9b',
                      strokeWidth: 2,
                      strokeDasharray: '5,5'
                    }
                  }))}
                >
                  <div>
                    <NetworkWidget
                      key={region.regionId}
                      isRegion={true}
                      customerId={customerId}
                      networkComponent={region}
                      networkId={selectId}
                      networkVersion={region.networkVersion}
                    />
                  </div>
                </ArcherElement>
              </div>
              <div className={classes.widjetWrap}>
                {region.gateways.map(gateway => (
                  <div
                    className={classes.gatewayWrap}
                    key={`wrap-${gateway.gatewayId}`}
                  >
                    <div className={classes.gatewayWrapCall}>
                      <ArcherElement
                        id={`gateway-${gateway.gatewayId}`}
                        relations={gateway.tunnels
                          .filter(tunnel => tunnel.connectedToGW)
                          .map(tunnel => ({
                            targetId: `tunnel-${tunnel.tunnelId}`,
                            sourceAnchor: 'left',
                            targetAnchor: 'left',
                            style: {
                              strokeColor: '#7f8c9b',
                              strokeWidth: 2,
                              strokeDasharray: '5,5'
                            }
                          }))}
                      >
                        <div>
                          <NetworkWidget
                            isCpuResizeEnabled={gateway.isCpuResizeEnabled}
                            key={gateway.gatewayId}
                            isGW={true}
                            customerId={customerId}
                            networkComponent={gateway}
                            networkId={selectId}
                            networkVersion={region.networkVersion}
                            regionName={region.providerRegionName}
                            setShowProgressBar={setShowProgressBar}
                          />
                        </div>
                      </ArcherElement>
                    </div>
                    <div className={classes.gatewayWrapCall}>
                      {gateway.tunnels.map(tunnel => (
                        <ArcherElement
                          key={tunnel.tunnelId}
                          id={`tunnel-${tunnel.tunnelId}`}
                        >
                          <div>
                            <NetworkWidget
                              key={tunnel.tunnelId}
                              isTunnel={true}
                              customerId={customerId}
                              networkComponent={tunnel}
                              networkId={selectId}
                              resize={true}
                              setShowProgressBar={setShowProgressBar}
                              networkVersion={region.networkVersion}
                            />
                          </div>
                        </ArcherElement>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ArcherContainer>
    </div>
  );
};

export default Graf;
